import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  LoadingController,
  ModalController,
  NavController,
  NavParams,
  Platform,
  ToastController,
} from '@ionic/angular';
import html2pdf from 'html2pdf.js';
import { Subscription } from 'rxjs';
import { PageCaptureService } from 'services/page-capture-service/page-capture.service';
import { CommonPullupMenuComponent } from 'src/app/pages/modal-screens/common-pullup-menu/common-pullup-menu.component';
import { mygolfUtils } from 'src/custom/mygolf2u-utils';
import { CompetitionRoundCourseRatings } from 'src/data/mygolf.data';
import { MessageDisplayUtil } from 'src/message-display-utils';
import * as moment from 'moment';

@Component({
  selector: 'app-description-modal',
  templateUrl: './description-modal.page.html',
  styleUrls: ['./description-modal.page.scss'],
})
export class DescriptionModalPage implements OnInit {
  public title: string;
  public descriptionText: string;
  public headerName: string;

  @Input() descriptionMenuActive: string;
  @Input() competition: any;
  @Input() details: any;
  @Input() roundCourseRatings: Array<CompetitionRoundCourseRatings>;
  @Input() message: any;
  @Input() totalRounds: number;
  @Input() isError: boolean = false;
  @Input() isWarning: boolean = false;
  @ViewChild('captureArea', { read: ElementRef }) captureArea!: ElementRef;
  isCapturing = false;

  constructor(
    public nav: NavController,
    private navParams: NavParams,
    private viewCtrl: ModalController,
    private modalCtrl: ModalController,
    private platform: Platform,
    private toastCtl: ToastController,
    private mg2uUtils: mygolfUtils,
    private pageCaptureService: PageCaptureService,
    private loadingController: LoadingController
  ) {
    this.title = this.navParams.get('title');
    this.descriptionText = this.navParams.get('description');
    this.headerName = this.navParams.get('headerName');
  }
  close() {
    this.modalCtrl.dismiss();
  }

  getInnerHTML() {
    let description =
      "<p><span style='background-color:#00FFFF'>Tournaments, electronic scorecards, analysis and more&hellip; Look no further! myGolf2u is a single place where you can find and register for any </span>competitions in your region, your favourite clubs or in the entire country on your phone or in this site.</p>";
    return description;
  }

  ngOnInit() {}

  confirm(value: string) {
    this.modalCtrl.dismiss(value);
  }

  getRoundCourseRatings(roundNo: number) {
    let _filtered = [];
    if (this.roundCourseRatings?.length > 0) {
      _filtered = this.roundCourseRatings.filter((courseRatings: CompetitionRoundCourseRatings) => {
        return courseRatings.roundNo === roundNo;
      });
    } else _filtered = this.roundCourseRatings;
    return _filtered;
  }

  getTeeColor(color: string, type?: string) {
    let teeColor: string;

    if (color === 'White') {
      if (type === 'player') teeColor = 'tee-white-tee-bg';
      else teeColor = 'tee-white';
      return teeColor;
    } else if (color === 'Red') {
      return 'tee-red';
    } else if (color === 'Black') {
      return 'tee-black';
    } else if (color === 'Gold') {
      if (type === 'info') teeColor = 'tee-gold-tee-bg';
      else teeColor = 'tee-gold';
      return teeColor;
    } else if (color === 'Blue') {
      return 'tee-blue';
    }
  }

  getBtnTbox(color: string) {
    let tboxStyle: string;

    if (color === 'White') {
      return 'button-teebox ' + 'button-teebox-white';
    } else return 'button-teebox';
  }
  getTeeColorBG(teeColor: string, teeName?: string) {
    if (teeName === 'White') return '#231f20';
    const _color = teeColor || '#000000';
    if (this.mg2uUtils.lightOrDark(_color) === 'light') {
      return '#231f20';
    } else {
      return '#ffffff';
    }
  }

  async captureAsImage(): Promise<void> {
    await this.performCapture('image');
  }

  async captureAsPdf(): Promise<void> {
    await this.performCapture('pdf');
  }

  private async performCapture(format: 'image' | 'pdf'): Promise<void> {
    if (this.isCapturing) return;

    this.isCapturing = true;
    const loading = await this.loadingController.create({
      message: format === 'image' ? 'Preparing screenshot...' : 'Preparing PDF...',
      spinner: 'crescent',
      duration: 5000,
    });

    await loading.present();

    try {
      const currentDateTime = moment().format('YYYYMMDD_HHmmss');
      const compInfoType = this.descriptionMenuActive;

      const filename = `comp${compInfoType}_${currentDateTime}`;
      const element = this.captureArea?.nativeElement;
      if (!element) {
        throw new Error('Content area not found');
      }

      // Dismiss loading BEFORE capture to avoid capturing the loading dialog
      // await loading.dismiss();

      // Wait for dismiss animation to complete and UI to settle
      await new Promise(resolve => setTimeout(resolve, 400));

      // For full-page capture, we need to get the scroll content
      // Try to get the parent ion-content to get scroll dimensions
      const ionContent = element.closest('ion-content');
      const scrollElement = ionContent ? (ionContent as any).scrollElement : null;

      // If we found the scroll element, use it for capture to get full height
      const captureElement = scrollElement || element;

      // Perform the capture
      const blob =
        format === 'image'
          ? await this.pageCaptureService.capturePageAsImage({
              element: captureElement,
              filename,
              format: 'image',
              quality: 0.95,
              backgroundColor: '#ffffff',
              delay: 300, // Reduced delay since we already waited
            })
          : await this.pageCaptureService.capturePageAsPdf({
              element: captureElement,
              filename,
              format: 'pdf',
              delay: 300, // Reduced delay since we already waited
            });

      if (blob) {
        await this.pageCaptureService.saveAndShare(blob, filename, format);

        const message =
          format === 'image' ? 'Screenshot captured successfully!' : 'PDF generated successfully!';

        await this.showToast(message, 'success');
      }
    } catch (error) {
      console.error('Capture error:', error);
      await this.showToast('Error capturing page. Please try again.', 'danger');
    } finally {
      // Ensure loading is dismissed in case of error
      try {
        await loading.dismiss();
      } catch (e) {
        // Loading already dismissed, ignore error
      }
      this.isCapturing = false;
    }
  }

  private async showToast(message: string, color: string): Promise<void> {
    const toast = await this.toastCtl.create({
      message,
      duration: 3000,
      color,
      position: 'bottom',
    });
    await toast.present();
  }

  /**
   * Export page as PDF using browser's native print functionality.
   * This captures the entire page including scrollable content.
   */
  async exportAsPdfNative(): Promise<void> {
    if (this.isCapturing) return;

    this.isCapturing = true;
    const loading = await this.loadingController.create({
      message: 'Preparing PDF...',
      spinner: 'crescent',
    });

    await loading.present();

    try {
      // Dismiss loading before print dialog
      await loading.dismiss();
      await new Promise(resolve => setTimeout(resolve, 200));

      // Store original body styles
      const originalBodyOverflow = document.body.style.overflow;
      const originalBodyHeight = document.body.style.height;

      // Ensure page is fully visible for print
      document.body.style.overflow = 'visible';
      document.body.style.height = 'auto';

      // Use browser's native print (which can save as PDF)
      window.print();

      // Restore body styles after a short delay
      setTimeout(() => {
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.height = originalBodyHeight;
      }, 1000);

      this.isCapturing = false;
    } catch (error) {
      console.error('Native PDF export error:', error);
      await this.showToast('Error exporting PDF. Please try again.', 'danger');
      this.isCapturing = false;
    }
  }

  /**
   * Export page as PDF using html2pdf.js library.
   * This library handles full-page HTML to PDF conversion better than manual capture.
   */
  async exportAsPdfWithLibrary(): Promise<void> {
    if (this.isCapturing) return;

    this.isCapturing = true;
    const loading = await this.loadingController.create({
      message: 'Generating PDF...',
      spinner: 'crescent',
    });

    await loading.present();

    try {
      const filename = `event-details-${new Date().getTime()}`;
      const element = this.captureArea?.nativeElement;
      if (!element) {
        throw new Error('Content area not found');
      }

      await loading.dismiss();
      await new Promise(resolve => setTimeout(resolve, 300));

      // For full-page capture, get the ion-content scroll element
      const ionContent = element.closest('ion-content');
      const scrollElement = ionContent ? (ionContent as any).scrollElement : null;
      const captureElement = scrollElement || element;

      const opt = {
        margin: [10, 10, 10, 10] as [number, number, number, number],
        filename: `${filename}.pdf`,
        image: {
          type: 'jpeg' as 'jpeg' | 'png' | 'webp',
          quality: 0.98,
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          scrollY: 0,
          scrollX: 0,
          windowHeight: captureElement.scrollHeight,
          windowWidth: captureElement.scrollWidth,
        },
        jsPDF: {
          unit: 'mm' as 'mm' | 'cm' | 'in' | 'pt' | 'px' | 'pc',
          format: 'a4' as string,
          orientation: 'portrait' as 'portrait' | 'landscape',
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] as ('avoid-all' | 'css' | 'legacy')[] },
      } as any; // Cast to any to bypass strict type checking for html2pdf.js

      await html2pdf().set(opt).from(captureElement).save();

      await this.showToast('PDF generated successfully!', 'success');
    } catch (error) {
      console.error('html2pdf error:', error);
      await this.showToast('Error generating PDF. Please try again.', 'danger');
    } finally {
      this.isCapturing = false;
    }
  }

  async moreOptions() {
    let _dataList = [
      {
        id: 'downloadPdf',
        name: 'Download Program Info (PDF)',
        // iconUrl: 'assets/icons/golf-website.png',
      },
      {
        id: 'downloadImage',
        name: 'Download Program Info (Image)',
        // iconUrl: 'assets/icons/book.png',
      },
    ];
    const modal = await this.modalCtrl.create({
      mode: 'ios',
      component: CommonPullupMenuComponent,
      cssClass: 'self-adjustable-modal',
      componentProps: {
        app: null,
        dataList: _dataList,
        mode: 'common',
        headerTitle: 'More Options',
        // subTitle: this.leagueRounds ? this.leagueRounds[0].seasonName : '',
      },
    });
    await modal.present().then(() => {
      const height = this.platform.height() - 250;
      document.getElementsByClassName('inner-content')[0]['style'].maxHeight = `${height}px`;
    });
    modal.onDidDismiss().then((data: any) => {
      console.debug('pullup dismiss', data);
      if (data) {
        switch (data?.data?.id) {
          case 'downloadPdf':
            this.performCapture('pdf');
            break;
          case 'downloadImage':
            this.performCapture('image');
            break;
        }
      }
    });
  }
}
